'use client';

// Pro Terminal "Volume" rail tab: a live, dynamically-refreshing top-N ranking
// of the universe by share or dollar volume for the current session (no fixed
// opening window — the value tracks tickers.volume, which priceSync keeps live).
// Backed by GET /api/markets/top-volume; SWR auto-refreshes ~60s and pauses
// while the tab is hidden. Controls: All/R1000 scope, $/shares metric, top-N.

import { useState } from 'react';
import useSWR from 'swr';
import { BarChart3, RefreshCw } from 'lucide-react';
import { getTopVolume, type TopVolumeRow } from '@/lib/api-client';
import { cn } from '@/lib/utils';

type Metric = 'dollars' | 'shares';
type Scope = 'r1000' | 'all';

const SCOPE_PRESETS: { value: Scope; label: string; title: string }[] = [
  { value: 'r1000', label: 'R1000', title: 'Russell 1000 (large caps)' },
  { value: 'all', label: 'All', title: 'All stocks' },
];
const METRIC_PRESETS: { value: Metric; label: string; title: string }[] = [
  { value: 'dollars', label: '$ Vol', title: 'Rank by dollar volume (shares × price)' },
  { value: 'shares', label: 'Shares', title: 'Rank by share volume' },
];

function fmtCompact(v: number | null | undefined): string {
  if (v == null || !Number.isFinite(v)) return '—';
  const a = Math.abs(v);
  if (a >= 1e9) return (v / 1e9).toFixed(2) + 'B';
  if (a >= 1e6) return (v / 1e6).toFixed(2) + 'M';
  if (a >= 1e3) return (v / 1e3).toFixed(1) + 'K';
  return String(Math.round(v));
}
function fmtPrice(v: number | null | undefined): string {
  if (v == null || !Number.isFinite(v)) return '—';
  return v >= 1000 ? v.toLocaleString(undefined, { maximumFractionDigits: 2 }) : v.toFixed(2);
}

export function VolumePanel({ activeSymbol, onSelectSymbol }: { activeSymbol: string; onSelectSymbol: (s: string) => void }) {
  const [n, setN] = useState(10);
  const [metric, setMetric] = useState<Metric>('dollars');
  const [scope, setScope] = useState<Scope>('r1000');

  // Key encodes every control so changing them refetches; SWR pauses while the
  // tab/window is hidden, so the ~60s poll is cheap.
  const { data, error, isLoading, isValidating } = useSWR<TopVolumeRow[]>(
    `topvol:${scope}:${metric}:${n}`,
    () => getTopVolume({ n, metric, scope }),
    { refreshInterval: 60_000, revalidateOnFocus: true, dedupingInterval: 30_000, keepPreviousData: true },
  );
  const rows = data ?? [];

  return (
    <div className="flex h-full flex-col">
      <div className="flex items-center gap-2 border-b border-default px-3 py-1.5">
        <BarChart3 size={13} className="text-sky-400" />
        <span className="text-xs font-semibold text-fg">Top Volume</span>
        <span className="text-[10px] text-slate-500">{metric === 'dollars' ? '$ volume' : 'shares'} · live</span>
        {isValidating && <RefreshCw size={11} className="ml-auto animate-spin text-slate-500" />}
      </div>

      <div className="flex flex-wrap items-center gap-1 border-b border-default px-3 py-1.5">
        <span className="mr-1 text-[9px] font-medium uppercase tracking-wide text-slate-500">Universe</span>
        {SCOPE_PRESETS.map((p) => (
          <button key={p.value} type="button" onClick={() => setScope(p.value)} title={p.title}
            className={cn('rounded-md px-2 py-0.5 text-[10px] font-medium transition-colors',
              scope === p.value ? 'bg-sky-500 text-white' : 'text-slate-400 hover:bg-card-bg hover:text-slate-200')}>
            {p.label}
          </button>
        ))}
        <span className="ml-2 mr-1 text-[9px] font-medium uppercase tracking-wide text-slate-500">By</span>
        {METRIC_PRESETS.map((p) => (
          <button key={p.value} type="button" onClick={() => setMetric(p.value)} title={p.title}
            className={cn('rounded-md px-2 py-0.5 text-[10px] font-medium transition-colors',
              metric === p.value ? 'bg-sky-500 text-white' : 'text-slate-400 hover:bg-card-bg hover:text-slate-200')}>
            {p.label}
          </button>
        ))}
        <span className="ml-2 mr-1 text-[9px] font-medium uppercase tracking-wide text-slate-500">Top</span>
        <input type="number" min={1} max={100} value={n}
          onChange={(e) => setN(Math.max(1, Math.min(100, Math.floor(Number(e.target.value)) || 1)))}
          className="w-14 rounded border border-default bg-surface px-1.5 py-0.5 text-right text-[11px] text-slate-200 focus:border-sky-500 focus:outline-none" />
      </div>

      <div className="min-h-0 flex-1 overflow-y-auto">
        {error ? (
          <div className="px-3 py-6 text-center text-xs text-rose-400">Failed to load volume ranking.</div>
        ) : isLoading && rows.length === 0 ? (
          <div className="px-3 py-6 text-center text-xs text-slate-500">Loading…</div>
        ) : rows.length === 0 ? (
          <div className="px-3 py-6 text-center text-xs text-slate-500">No data — markets closed or not yet synced.</div>
        ) : (
          <ul className="divide-y divide-default">
            {rows.map((r, i) => {
              const up = (r.changePct ?? 0) >= 0;
              const active = r.ticker === activeSymbol;
              const val = metric === 'dollars' ? '$' + fmtCompact(r.dollarVolume) : fmtCompact(r.volume);
              return (
                <li key={r.ticker}>
                  <button type="button" onClick={() => onSelectSymbol(r.ticker)}
                    className={cn('flex w-full items-center gap-2 px-3 py-1.5 text-left transition-colors hover:bg-card-bg',
                      active && 'border-l-2 border-sky-500 bg-sky-500/10')}>
                    <span className="w-5 shrink-0 text-right text-[10px] tabular-nums text-slate-500">{i + 1}</span>
                    <span className="w-14 shrink-0 truncate text-xs font-semibold text-fg" title={r.name}>{r.ticker}</span>
                    <span className="w-16 shrink-0 text-right text-xs font-medium tabular-nums text-sky-300">{val}</span>
                    <span className="ml-auto text-right text-[11px] tabular-nums text-slate-300">{fmtPrice(r.price)}</span>
                    <span className={cn('w-14 shrink-0 text-right text-[11px] tabular-nums', up ? 'text-emerald-400' : 'text-rose-400')}>
                      {up ? '+' : ''}{(r.changePct ?? 0).toFixed(2)}%
                    </span>
                  </button>
                </li>
              );
            })}
          </ul>
        )}
      </div>
    </div>
  );
}

export default VolumePanel;
